import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

import AdSlot from "../components/ad-slot";
import ArticleCard from "../components/article-card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  formatDate,
  getArticle,
  getRelatedArticles,
} from "../lib/articles";
import { usePageMeta } from "../lib/use-page-meta";

function ArticleBody({ blocks }) {
  return blocks.map((block, index) => {
    if (block.type === "h2") {
      return <h2 key={index}>{block.text}</h2>;
    }
    if (block.type === "ul") {
      return (
        <ul key={index}>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    }
    if (block.type === "quote") {
      return <blockquote key={index}>{block.text}</blockquote>;
    }
    return <p key={index}>{block.text}</p>;
  });
}

function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);

  usePageMeta({
    title: article?.title || "Article not found",
    description: article?.excerpt,
    path: `/articles/${slug}`,
  });

  if (!article) {
    return (
      <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          404
        </p>
        <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight">
          That article does not exist
        </h1>
        <p className="mt-3 max-w-md text-muted-foreground">
          The link may be old, or the piece may have moved. The archive is a good place
          to look next.
        </p>
        <Button asChild className="mt-6">
          <Link to="/articles">
            Browse the articles <ArrowRight />
          </Link>
        </Button>
      </div>
    );
  }

  const related = getRelatedArticles(article.slug);
  const splitAt = 4;

  return (
    <article className="container py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/articles"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="font-normal">
                {tag}
              </Badge>
            ))}
          </div>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <time dateTime={article.date}>{formatDate(article.date)}</time>
            <span aria-hidden="true">·</span>
            <span>{article.readingTime}</span>
            <span aria-hidden="true">·</span>
            <span>By Aiman</span>
          </div>
          <p className="mt-6 border-l-2 border-primary/40 pl-5 font-serif text-xl leading-relaxed text-foreground/75">
            {article.excerpt}
          </p>
        </header>

        <div className="prose-copy mt-8">
          <ArticleBody blocks={article.body.slice(0, splitAt)} />
        </div>

        <AdSlot className="px-0" />

        <div className="prose-copy">
          <ArticleBody blocks={article.body.slice(splitAt)} />
        </div>

        <div className="mt-12 flex flex-col gap-5 border-t pt-8 sm:flex-row sm:items-center">
          <img
            src="/logo192.png"
            alt=""
            width="56"
            height="56"
            className="h-14 w-14 rounded-full border object-cover"
          />
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Written by
            </p>
            <p className="mt-1 font-serif text-xl font-semibold">Aiman</p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Someone who likes learning, tinkering, and writing things down. Jitra,
              Kedah is home. Not a professional developer.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/about">About me</Link>
          </Button>
        </div>
      </div>

      <section className="mx-auto mt-16 max-w-5xl border-t pt-10">
        <h2 className="font-serif text-2xl font-semibold tracking-tight">
          Keep reading
        </h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {related.map((item) => (
            <ArticleCard key={item.slug} article={item} />
          ))}
        </div>
      </section>
    </article>
  );
}

export default ArticleDetail;
